import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

function CompareBuilder({ products, onInsert }) {
  const [ids, setIds] = useState([]);
  const byId = Object.fromEntries(products.map((p) => [p.id, p]));
  return (
    <div data-testid="post-compare-builder">
      <label className="block text-xs font-medium text-[#5C5A56] mb-1.5">Add a comparison table (pick 2–4 products)</label>
      <div className="flex gap-2">
        <select data-testid="post-compare-select" value="" onChange={(e) => e.target.value && !ids.includes(e.target.value) && ids.length < 4 && setIds([...ids, e.target.value])} className="flex-1 px-3 py-2 rounded-lg border border-[#E5E0D8] bg-white text-sm">
          <option value="">Add a product to compare…</option>
          {products.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
        </select>
        <button type="button" data-testid="post-insert-compare-btn" disabled={ids.length < 2} onClick={() => { onInsert(ids); setIds([]); }} className="hh-btn-primary rounded-full px-4 py-2 text-sm font-semibold whitespace-nowrap disabled:opacity-40">Insert table</button>
      </div>
      {ids.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {ids.map((id) => (
            <button type="button" key={id} data-testid={`post-compare-chip-${id}`} onClick={() => setIds(ids.filter((x) => x !== id))} className="text-xs bg-white border border-[#E5E0D8] rounded-full px-2.5 py-1">
              {byId[id]?.title?.slice(0, 28)} ✕
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function PostProductPicker({ form, setForm }) {
  const [products, setProducts] = useState([]);
  const [selected, setSelected] = useState("");

  useEffect(() => {
    api.get("/products", { params: { limit: 500 } }).then((r) => setProducts(r.data));
  }, []);

  const insertInline = () => {
    if (!selected) return toast.error("Pick a product first");
    const sep = form.content && !form.content.endsWith("\n") ? "\n\n" : "";
    setForm({ ...form, content: `${form.content || ""}${sep}[product:${selected}]\n\n` });
    toast.success("Product card added to the end of the content");
  };

  const related = form.related_product_ids || [];
  const toggle = (id) => setForm({
    ...form,
    related_product_ids: related.includes(id) ? related.filter((x) => x !== id) : [...related, id],
  });

  return (
    <div className="rounded-xl border border-[#E5E0D8] bg-[#F9F8F6] p-4 space-y-4" data-testid="post-product-picker">
      <div>
        <label className="block text-xs font-medium text-[#5C5A56] mb-1.5">Add a product card inside the article</label>
        <div className="flex gap-2">
          <select data-testid="post-inline-product-select" value={selected} onChange={(e) => setSelected(e.target.value)} className="flex-1 px-3 py-2 rounded-lg border border-[#E5E0D8] bg-white text-sm">
            <option value="">Choose a product…</option>
            {products.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
          </select>
          <button type="button" data-testid="post-insert-product-btn" onClick={insertInline} className="hh-btn-primary rounded-full px-4 py-2 text-sm font-semibold whitespace-nowrap">Insert card</button>
        </div>
        <p className="text-xs text-[#5C5A56] mt-1.5">Adds a [product:…] line — move it to any spot between paragraphs.</p>
      </div>
      <CompareBuilder products={products} onInsert={(ids) => {
        const sep = form.content && !form.content.endsWith("\n") ? "\n\n" : "";
        setForm({ ...form, content: `${form.content || ""}${sep}[compare:${ids.join(",")}]\n\n` });
        toast.success("Comparison table added to the end of the content");
      }} />
      <div>
        <label className="block text-xs font-medium text-[#5C5A56] mb-1.5">Editor's picks at the end of the article ({related.length} selected)</label>
        <div className="max-h-44 overflow-y-auto grid sm:grid-cols-2 gap-1.5 bg-white border border-[#E5E0D8] rounded-lg p-2">
          {products.map((p) => (
            <label key={p.id} className="flex items-center gap-2 text-xs text-[#2C2B29] cursor-pointer">
              <input type="checkbox" data-testid={`post-related-${p.id}`} checked={related.includes(p.id)} onChange={() => toggle(p.id)} />
              <span className="truncate">{p.title}</span>
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}
